import type { MenuItem } from './types';
import { getCategoriesCatalogApi, getItemsApi, type ApiItemRow } from '../../../services/itemCatalogApi';

export const mockMenuItems: MenuItem[] = [
  // Coffee (5)
  { id: 'menu-1', name: 'House Drip Coffee', category: 'Coffee', price: 3.50 },
  { id: 'menu-2', name: 'Caramel Latte', category: 'Coffee', price: 5.75 },
  { id: 'menu-3', name: 'Signature Mocha', category: 'Coffee', price: 6.25 },
  { id: 'menu-4', name: 'Cold Brew', category: 'Coffee', price: 4.50 },
  { id: 'menu-5', name: 'Espresso Double Shot', category: 'Coffee', price: 3.75 },

  // Food (4)
  { id: 'menu-6', name: 'Avocado Toast', category: 'Food', price: 9.50 },
  { id: 'menu-7', name: 'Turkey Club Sandwich', category: 'Food', price: 11.25 },
  { id: 'menu-8', name: 'Caesar Salad', category: 'Food', price: 8.75 },
  { id: 'menu-9', name: 'Breakfast Burrito', category: 'Food', price: 10.00 },

  // Desserts (3)
  { id: 'menu-10', name: 'New York Cheesecake', category: 'Desserts', price: 7.50 },
  { id: 'menu-11', name: 'Chocolate Brownie', category: 'Desserts', price: 5.25 },
  { id: 'menu-12', name: 'Tiramisu', category: 'Desserts', price: 8.00 },

  // Beverages (3)
  { id: 'menu-13', name: 'Fresh Squeezed OJ', category: 'Beverages', price: 4.75 },
  { id: 'menu-14', name: 'Berry Smoothie', category: 'Beverages', price: 6.50 },
  { id: 'menu-15', name: 'Iced Tea', category: 'Beverages', price: 3.25 },

  // Specials (3)
  { id: 'menu-16', name: 'Seasonal Pumpkin Latte', category: 'Specials', price: 6.75 },
  { id: 'menu-17', name: 'Chef\'s Brunch Plate', category: 'Specials', price: 14.50 },
  { id: 'menu-18', name: 'Matcha Affogato', category: 'Specials', price: 7.25 },
];

function toNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeCategoryRows(raw: unknown): Array<{ id?: string; name: string }> {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const cat = entry as Record<string, unknown>;
      const id = String(cat.id ?? cat.categoryId ?? cat.category_id ?? '').trim() || undefined;
      const name = String(cat.name ?? cat.categoryName ?? cat.category_name ?? '').trim();
      if (!name) return null;
      return { id, name };
    })
    .filter((row): row is { id: string | undefined; name: string } => Boolean(row?.name));
}

type CategoryMeta = { name: string; sortOrder: number };

/**
 * Module-level cache: categoryName (lower-cased) → sortOrder from the categories API.
 * Populated every time getOfferMenuItems resolves. Used by CategoryItemPicker to sort
 * groups even when per-item category lookup misses.
 */
let cachedCategorySortOrder: Record<string, number> = {};

/** Returns a snapshot of the category-name → sortOrder map (keys are lower-cased). */
export function getCachedCategorySortOrder(): Record<string, number> {
  return cachedCategorySortOrder;
}

function buildItemToCategoryNamesLookup(
  categoriesPayload: Awaited<ReturnType<typeof getCategoriesCatalogApi>> | null,
): Map<string, CategoryMeta[]> {
  const itemToCategories = new Map<string, Map<string, CategoryMeta>>();
  if (!categoriesPayload) return new Map();
  const allCategories = [...(categoriesPayload.parentcatagory ?? []), ...(categoriesPayload.subcategory ?? [])];

  // ── Build the global name→sortOrder cache from every category row ──
  const newSortOrderCache: Record<string, number> = {};
  allCategories.forEach((cat, idx) => {
    const categoryName = String(cat.name ?? '').trim();
    if (!categoryName) return;
    const sortOrder = cat.sortOrder != null ? Number(cat.sortOrder) : idx;
    newSortOrderCache[categoryName.toLowerCase()] = sortOrder;

    (cat.items ?? []).forEach((row) => {
      const itemId = String(row.itemID ?? row.itemId ?? '').trim();
      if (!itemId) return;
      if (!itemToCategories.has(itemId)) itemToCategories.set(itemId, new Map());
      itemToCategories.get(itemId)!.set(categoryName, { name: categoryName, sortOrder });
    });
  });
  cachedCategorySortOrder = newSortOrderCache;

  const out = new Map<string, CategoryMeta[]>();
  itemToCategories.forEach((catMap, itemId) => {
    out.set(itemId, Array.from(catMap.values()).sort((a, b) => a.sortOrder - b.sortOrder));
  });
  return out;
}

function mapApiItemToOfferMenuItem(item: ApiItemRow, itemToCategoryNames: Map<string, CategoryMeta[]>): MenuItem | null {
  const id = item.itemId ?? item.id;
  const name = item.name?.trim();
  if (!id || !name) return null;
  const fromItem = normalizeCategoryRows((item as Record<string, unknown>).categories);
  // Prefer categories from the dedicated categories API (which carries sortOrder);
  // fall back to inline item fields when the categories API didn't return data.
  const fromCategoriesApi = (itemToCategoryNames.get(String(id)) ?? []).map((meta) => ({
    id: undefined as string | undefined,
    name: meta.name,
    sortOrder: meta.sortOrder,
  }));
  const categories: MenuItem['categories'] = fromCategoriesApi.length > 0 ? fromCategoriesApi : fromItem;
  const firstCategory = categories?.[0];
  const category = firstCategory?.name?.trim() || 'Uncategorized';
  const categoryId = firstCategory?.id ? String(firstCategory.id).trim() : undefined;
  const price = toNumber(item.pricing?.basePrice, 0);
  return { id: String(id), name, category, categoryId, categories, price };
}

export async function getOfferMenuItems(locationId: string, token?: string | null): Promise<MenuItem[]> {
  const [apiItems, categoriesPayload] = await Promise.all([
    getItemsApi(locationId, token),
    getCategoriesCatalogApi(locationId, token).catch(() => null),
  ]);
  const itemToCategoryNames = buildItemToCategoryNamesLookup(categoriesPayload);
  const mapped = apiItems
    .map((item) => mapApiItemToOfferMenuItem(item, itemToCategoryNames))
    .filter((item): item is MenuItem => Boolean(item));
  return mapped;
}

// ── Module-level cache so the list page can preload once and other screens
//    (form, detail) consume the cached menu items without re-fetching. ──
let cachedMenuItems: MenuItem[] = [];
let cachedLocationId: string | null = null;
let inFlight: Promise<MenuItem[]> | null = null;
const listeners = new Set<(items: MenuItem[]) => void>();

function notify(items: MenuItem[]) {
  listeners.forEach((l) => {
    try { l(items); } catch { /* ignore listener errors */ }
  });
}

export function getCachedOfferMenuItems(): MenuItem[] {
  return cachedMenuItems;
}

export function subscribeOfferMenuItems(listener: (items: MenuItem[]) => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function clearOfferMenuItemsCache(): void {
  cachedMenuItems = [];
  cachedLocationId = null;
  notify(cachedMenuItems);
}

export function loadOfferMenuItems(
  locationId: string,
  token?: string | null,
  options: { force?: boolean } = {},
): Promise<MenuItem[]> {
  const { force = false } = options;
  if (!locationId) return Promise.resolve(cachedMenuItems);
  if (!force && cachedLocationId === locationId && cachedMenuItems.length > 0) {
    return Promise.resolve(cachedMenuItems);
  }
  if (inFlight && cachedLocationId === locationId && !force) return inFlight;
  inFlight = getOfferMenuItems(locationId, token)
    .then((items) => {
      cachedMenuItems = items;
      cachedLocationId = locationId;
      notify(cachedMenuItems);
      return items;
    })
    .catch((err) => {
      cachedMenuItems = [];
      cachedLocationId = locationId;
      notify(cachedMenuItems);
      throw err;
    })
    .finally(() => { inFlight = null; });
  return inFlight;
}
